<template>
  <div>
    <button class="btn btn-success" @click="openModal" :title="$t('global.exporttoexcel')">
      <font-awesome-icon icon="fa-duotone fa-file-spreadsheet" class="mr-2" />
      Excel
    </button>

    <div :id="modalId" class="modal fade" tabindex="-1">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <modal-close-button @confirmed="closeModal" />
            <h4>
              <font-awesome-icon icon="fa-duotone fa-file-spreadsheet" class="mr-2" />
              Excel generieren
            </h4>
          </div>

          <div class="modal-body">
            <!-- Basis-Spalten -->
            <div v-if="config.baseColumns && config.baseColumns.length">
              <div class="d-flex align-items-baseline mb-2">
                <h6 class="font-weight-bold mb-0 mr-2">Basis-Spalten</h6>
                <small>
                  <a href="#" @click.prevent="selectedColumns = config.baseColumns.map(c => c.key)">Alle</a>
                  &nbsp;/&nbsp;
                  <a href="#" @click.prevent="selectedColumns = []">Keine</a>
                </small>
              </div>
              <div class="row mb-3">
                <div
                  v-for="col in config.baseColumns"
                  :key="col.key"
                  class="col-xl-4 col-md-6"
                >
                  <b-form-checkbox v-model="selectedColumns" :value="col.key">
                    {{ col.label }}
                  </b-form-checkbox>
                </div>
              </div>
              <hr />
            </div>

            <!-- Zusatzfelder (pro Zeile) -->
            <div v-if="config.additionalFields && config.additionalFields.length">
              <div class="d-flex align-items-baseline mb-2">
                <h6 class="font-weight-bold mb-0 mr-2">
                  Zusatzfelder
                  <small class="text-muted font-weight-normal">(werden pro Zeile berechnet)</small>
                </h6>
                <small>
                  <a href="#" @click.prevent="selectedAdditional = config.additionalFields.map(f => f.key)">Alle</a>
                  &nbsp;/&nbsp;
                  <a href="#" @click.prevent="selectedAdditional = []">Keine</a>
                </small>
              </div>
              <div class="row mb-3">
                <div
                  v-for="field in config.additionalFields"
                  :key="field.key"
                  class="col-xl-4 col-md-6"
                >
                  <b-form-checkbox v-model="selectedAdditional" :value="field.key">
                    {{ field.label }}
                  </b-form-checkbox>
                </div>
              </div>
              <hr />
            </div>

            <!-- Zusammenfassung (Sheet 2) -->
            <div v-if="config.summaryFields && config.summaryFields.length">
              <div class="d-flex align-items-baseline mb-2">
                <h6 class="font-weight-bold mb-0 mr-2">
                  Zusammenfassung
                  <small class="text-muted font-weight-normal">(erscheint als separates Tabellenblatt)</small>
                </h6>
                <small>
                  <a href="#" @click.prevent="selectedSummary = config.summaryFields.map(f => f.key)">Alle</a>
                  &nbsp;/&nbsp;
                  <a href="#" @click.prevent="selectedSummary = []">Keine</a>
                </small>
              </div>

              <!-- Gruppiert (wenn mindestens ein Feld eine group-Property hat) -->
              <template v-if="summaryHasGroups">
                <div v-for="group in summaryGroups" :key="group.label || '__ungrouped__'" class="mb-2">
                  <div v-if="group.label" class="d-flex align-items-center mb-1">
                    <small class="text-muted font-weight-bold text-uppercase mr-2" style="letter-spacing:.04em">{{ group.label }}</small>
                    <div style="flex:1;border-top:1px solid #dee2e6"></div>
                    <small class="ml-2">
                      <a href="#" @click.prevent="selectGroup(group.fields, true)">Alle</a>
                      &nbsp;/&nbsp;
                      <a href="#" @click.prevent="selectGroup(group.fields, false)">Keine</a>
                    </small>
                  </div>
                  <div class="row">
                    <div
                      v-for="field in group.fields"
                      :key="field.key"
                      class="col-xl-6 col-md-6"
                    >
                      <b-form-checkbox v-model="selectedSummary" :value="field.key">
                        {{ field.label }}
                      </b-form-checkbox>
                    </div>
                  </div>
                </div>
              </template>

              <!-- Ungroupiert (klassisch, wie bisher) -->
              <template v-else>
                <div class="row mb-2">
                  <div
                    v-for="field in config.summaryFields"
                    :key="field.key"
                    class="col-xl-6 col-md-6"
                  >
                    <b-form-checkbox v-model="selectedSummary" :value="field.key">
                      {{ field.label }}
                    </b-form-checkbox>
                  </div>
                </div>
              </template>
              <hr />
            </div>

            <!-- Stichtag (optional, nur wenn config.stichtag === true) -->
            <div v-if="config.stichtag">
              <h6 class="font-weight-bold mb-2">
                Stichtag
                <small class="text-muted font-weight-normal">
                  ({{ config.stichtagHint || "optional – berücksichtigt nur am gewählten Datum aktive Einträge" }})
                </small>
              </h6>
              <div class="row mb-2">
                <div class="col-xl-4 col-md-6">
                  <input type="date" v-model="stichtag" class="form-control" />
                </div>
              </div>
              <div v-if="stichtagOutsideSeason" class="text-warning small mb-2">
                Der Stichtag liegt ausserhalb der gewählten Saison – das Ergebnis ist leer.
              </div>
            </div>

            <!-- Saison (optional, nur wenn config.seasonSelect === true) -->
            <div v-if="config.seasonSelect">
              <h6 class="font-weight-bold mb-2">
                Saison
                <small class="text-muted font-weight-normal">
                  ({{ config.seasonHint || "optional – schränkt auf die gewählte Saison (01.11.–31.10.) ein" }})
                </small>
              </h6>
              <div class="row mb-2">
                <div class="col-xl-4 col-md-6">
                  <select v-model="selectedSeason" class="form-control">
                    <option :value="null">-- keine Einschränkung --</option>
                    <option v-for="s in availableSeasons" :key="s" :value="s">
                      Saison {{ s + 1 }} (01.11.{{ s }} – 31.10.{{ s + 1 }})
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div v-if="noColumnsSelected" class="alert alert-warning mt-2 mb-0">
              Bitte mindestens eine Basis-Spalte auswählen.
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn btn-default" @click="closeModal" :disabled="loading">
              {{ $t("global.cancel") }}
            </button>
            <button
              class="btn btn-success"
              @click="exportExcel"
              :disabled="loading || noColumnsSelected"
            >
              <font-awesome-icon
                v-if="loading"
                icon="fa-duotone fa-spinner"
                spin
                class="mr-2"
              />
              <font-awesome-icon v-else icon="fa-duotone fa-file-spreadsheet" class="mr-2" />
              {{ loading ? "Wird generiert..." : "Exportieren" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import FileDownload from "js-file-download";
import ExportApi from "@/api/ExportApi";
import ModalCloseButton from "@/components/global/ModalCloseButton";

export default {
  name: "ExcelExportModal",
  components: { ModalCloseButton },
  props: {
    filters: {
      type: Object,
      default: () => ({}),
    },
    config: {
      type: Object,
      required: true,
      // Erwartete Struktur:
      // {
      //   endpoint: String,
      //   filename: String,
      //   baseColumns: [{ key, label }],
      //   additionalFields: [{ key, label }],   // optional
      //   summaryFields: [{ key, label }],       // optional
      // }
    },
  },
  data() {
    return {
      selectedColumns: [],
      selectedAdditional: [],
      selectedSummary: [],
      stichtag: null,
      availableSeasons: [],
      selectedSeason: null,
      seasonPreselected: false,
      loading: false,
    };
  },
  computed: {
    modalId() {
      return `excel-export-modal-${this.config.endpoint}`;
    },
    stichtagOutsideSeason() {
      if (!this.config.seasonSelect || !this.stichtag || this.selectedSeason === null) return false;
      return (
        this.stichtag < `${this.selectedSeason}-11-01` ||
        this.stichtag > `${this.selectedSeason + 1}-10-31`
      );
    },
    noColumnsSelected() {
      return this.selectedColumns.length === 0;
    },
    summaryHasGroups() {
      return (this.config.summaryFields || []).some((f) => f.group);
    },
    summaryGroups() {
      const groupMap = {};
      const groupOrder = [];
      const ungrouped = [];
      (this.config.summaryFields || []).forEach((field) => {
        if (field.group) {
          if (!groupMap[field.group]) {
            groupMap[field.group] = [];
            groupOrder.push(field.group);
          }
          groupMap[field.group].push(field);
        } else {
          ungrouped.push(field);
        }
      });
      const result = [];
      if (ungrouped.length) result.push({ label: null, fields: ungrouped });
      groupOrder.forEach((g) => result.push({ label: g, fields: groupMap[g] }));
      return result;
    },
  },
  watch: {
    "config.baseColumns": {
      immediate: true,
      handler(cols) {
        if (cols && cols.length) {
          this.selectedColumns = cols.map((c) => c.key);
        }
      },
    },
    "config.endpoint": {
      immediate: true,
      handler() {
        if (this.config.seasonSelect) this.loadSeasons();
      },
    },
  },
  methods: {
    loadSeasons() {
      ExportApi.get(`/excel/${this.config.endpoint}-saisons`)
        .then((response) => {
          this.availableSeasons = response.data || [];
          // Default: aktuelle Saison (01.11.-31.10.), sofern Daten dazu existieren
          if (this.selectedSeason === null && !this.seasonPreselected) {
            this.seasonPreselected = true;
            const now = new Date();
            const current = now.getMonth() >= 10 ? now.getFullYear() : now.getFullYear() - 1;
            if (this.availableSeasons.includes(current)) this.selectedSeason = current;
          }
        })
        .catch(() => {
          this.availableSeasons = [];
        });
    },
    selectGroup(fields, select) {
      const keys = fields.map((f) => f.key);
      if (select) {
        const toAdd = keys.filter((k) => !this.selectedSummary.includes(k));
        this.selectedSummary = [...this.selectedSummary, ...toAdd];
      } else {
        this.selectedSummary = this.selectedSummary.filter((k) => !keys.includes(k));
      }
    },
    openModal() {
      $(`#${this.modalId}`).modal({ backdrop: "static", keyboard: false });
    },
    closeModal() {
      $(`#${this.modalId}`).modal("hide");
    },
    exportExcel() {
      if (this.noColumnsSelected) return;
      this.loading = true;

      // Entferne null/undefined-Werte aus den Filtern
      const cleanFilters = Object.fromEntries(
        Object.entries(this.filters).filter(
          ([, v]) => v !== null && v !== undefined && v !== ""
        )
      );

      // Saison überschreibt Von/Bis mit den Saisongrenzen (01.11.-31.10.)
      if (this.config.seasonSelect && this.selectedSeason !== null) {
        const keys = this.config.seasonFilterKeys || {};
        if (keys.von) cleanFilters[keys.von] = `${this.selectedSeason}-11-01`;
        if (keys.bis) cleanFilters[keys.bis] = `${this.selectedSeason + 1}-10-31`;
      }

      const payload = {
        filters: cleanFilters,
        selectedColumns: this.selectedColumns,
        additionalFields: this.selectedAdditional,
        summaryFields: this.selectedSummary,
        stichtag: this.stichtag || null,
      };

      ExportApi.post("/excel/" + this.config.endpoint, payload, {
        responseType: "blob",
      })
        .then((response) => {
          const date = new Date().toLocaleDateString("de-CH");
          FileDownload(
            response.data,
            `${this.config.filename}_${date}.xlsx`
          );
          this.closeModal();
        })
        .catch(() => {
          this.$notify({
            type: "error",
            title: "Export fehlgeschlagen",
            text: "Fehler beim Erstellen der Excel-Datei.",
            duration: 7000,
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>
